import { useState, useMemo, useRef, useEffect, useCallback, memo } from 'react';
import { useParams } from 'react-router-dom';

import { AxiosResponse } from 'axios';
import { AgGridReact } from 'ag-grid-react';
import { GetRowIdParams, ICellRendererParams, SelectionChangedEvent } from 'ag-grid-community';

import { Modal, Box, ModalFooter, ModalHeader, BodySmall, StatusSelect, Select, SelectChangeEvent, AgGridTable, ColDef, Button, Dayjs, CustomDateTimePicker, Tooltip } from '@parspec/pixel';

import { INITIAL_PROJECT_STAGE, getProjectStages, QUOTE, PROJECT_STAGE_TRIGGER_VALUES } from '../constants';
import { useSelectedQuoteView } from 'src/app/Context';
import { useGetQuotesQuery, useDocumentTags } from '../../Dashboard/queries';
import { useUpdateProjectTagMutation, useGetProjectBomsQuery } from '../../Project/queries';
import { ProjectQuotesInfo } from '../../Dashboard/queries/apiTypes';
import { convertDateFormat, getDocumentOptions, validateProjectAndBomStatus } from '../utils/utils';
import { IUpdateDocumentTag } from '../../Project/queries/apiTypes';
import { useFeatureFlag } from '../UserActivityTracking/useFeatureFlag';
import { useAllTagsHook } from '../hooks/useAllTagsHook';
import { useUnlinkBOMMutation } from '../../Project/queries';
import { QUOTE_DOCUMENT_AUTOMATION_STATUSES } from '../utils/constants';
import { STATUS_RECONCILIATION_POPUP_CONSTANTS } from './constants';
import { CustomNoRowsOverlayComponent } from 'src/features/Project/BomTable/components';
import { ButtonTypes, IOptionType } from './types';
import { LearnMore } from '../LearnMore';
import { getValueKeyFromMap, getStatusReconciliationModalTableHeight } from './utils';
import { BomTemplate, Customer } from 'src/features/Dashboard/shared/components';
import { getIfWeekendDates } from '../CreateAndEditProjectModal/utils';
import { isEqual } from 'lodash-es';
import CustomToolBarPanel from '../CustomToolBarPanel';

interface IUpdateProjectQuoteStatusModal {
    open: boolean;
    onClose: () => void;
    onUpdateProjectStage: (value: string, bidDueDate?: Dayjs | null) => void;
    isUpdatingProjectDetails: boolean;
    selectedProjectStage?: string;
    projectId?: string | number;
    apiBidDueDate: Dayjs | null;
}

function UpdateProjectQuoteStatusModal({
    open,
    onClose,
    onUpdateProjectStage,
    isUpdatingProjectDetails,
    selectedProjectStage = INITIAL_PROJECT_STAGE,
    projectId = '',
    apiBidDueDate
}: IUpdateProjectQuoteStatusModal) {
    const { projectId: paramsProjectId = '' } = useParams();
    const currentProjectId = projectId || paramsProjectId

    const { enable_quoting, isLoading: isEnableQuotingLoading } = useFeatureFlag();
    const { quoteSelectedView, isDashboardViewsLoading } = useSelectedQuoteView();

    const gridRef = useRef<AgGridReact>(null);

    const [projectStage, setProjectStage] = useState(selectedProjectStage);
    const [bidDueDate, setBidDueDate] = useState<Dayjs | null>(apiBidDueDate);
    const [showBidDueDate, setShowBidDueDate] = useState(false);
    const [quotesData, setQuotesData] = useState<ProjectQuotesInfo[]>([]);
    const [error, setError] = useState({ error: '' });
    const [selectedBoms, setSelectedBoms] = useState<number[]>([]);

    const { data: projectQuotesData, isLoading: isProjectQuotesDataLoading } = useGetQuotesQuery(quoteSelectedView, { enabled: !isDashboardViewsLoading });
    const { data: quotesTagData, isLoading: isQuotesTagLoading } = useDocumentTags(QUOTE);
    const { data: projectBomsData, isLoading: isBomDataLoading } = useGetProjectBomsQuery(Number(currentProjectId), {
        enabled: Boolean(Number(currentProjectId))
    });

    const { mutateAsync: updateProjectQuotesTag, isLoading: isUpdatingDocumentTags } = useUpdateProjectTagMutation();
    const { mutateAsync: unLinkBomDetails } = useUnlinkBOMMutation();

    const { allTagsObject, allTagsData } = useAllTagsHook();
    const quotesStatusOptions = useMemo(() => {
        if (allTagsData) {
            return getDocumentOptions(allTagsData, QUOTE);
        }

        return []
    }, [allTagsData])

    const isTableLoading = useMemo(() => {
        return isProjectQuotesDataLoading || isQuotesTagLoading || isBomDataLoading || isEnableQuotingLoading;
    }, [isBomDataLoading, isEnableQuotingLoading, isProjectQuotesDataLoading, isQuotesTagLoading])

    const isFooterLoading = isUpdatingProjectDetails || isUpdatingDocumentTags;

    useEffect(() => {
        if (selectedProjectStage === STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_STAGE_REBID) {
            setShowBidDueDate(true);
        }
    }, [selectedProjectStage])

    const getQuoteStatusTemplate = useCallback((props: ICellRendererParams) => {
        const { data } = props
        const fieldValue = data?.selectedQuoteStatus;

        function handleOnSelect(e: SelectChangeEvent) {
            const currentQuotesData = [...quotesData]
            if (currentQuotesData.length) {
                currentQuotesData.forEach((quotesRowData) => {
                    if (quotesRowData.bom_id === data.bom_id) {
                        quotesRowData.selectedQuoteStatus = Number(e.target.value);
                    }
                });

                setError({ error: '' });
                setQuotesData(currentQuotesData);
            }

        }

        return (
            <Box display={'flex'} alignItems={'center'} width={1} height={1}>
                {
                    fieldValue != null ? <StatusSelect size="small" value={fieldValue} options={quotesStatusOptions as unknown as IOptionType[]} optionLabelKeyname="label" optionValueKeyname="value" onChange={handleOnSelect} sx={{ height: 28 }} /> : null
                }
            </Box>
        );
    }, [quotesData, quotesStatusOptions]);

    const getQuotesTemplate = useCallback((rowParams: ICellRendererParams) => {
        const { data: rowData } = rowParams

        function handleOnSelect(e: SelectChangeEvent<unknown>) {
            const currentQuotesData = [...quotesData || []]
            currentQuotesData.forEach((quoteRow) => {
                if (quoteRow.bom_id === rowData.bom_id) {
                    quoteRow.selectedQuoteId = Number(e.target.value);
                }
            });
            setQuotesData(currentQuotesData);
        }
        return (
            <Box display={'flex'} alignItems={'center'} height={1}>
                {rowData.selectedQuoteId ? (
                    <Select sx={{ height: 28 }} size="small" value={rowData.selectedQuoteId} options={rowData.quotes} optionLabelKeyname="quote_number" optionValueKeyname="id" onChange={handleOnSelect} />
                ) : (

                    <BodySmall sx={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textWrap: 'nowrap' }} limit={false}>There are no quotes in this BOM</BodySmall>

                )}
            </Box>
        );
    }, [quotesData]);

    const columnDefs = useMemo((): ColDef[] => ([
        {
            field: 'bom_id',
            hide: true,
            lockPosition: 'left',
            resizable: false
        },
        { headerCheckboxSelection: true, checkboxSelection: true, width: 50, maxWidth: 50, resizable: false, hide: false, lockPosition: 'left' },
        {
            field: 'selectedQuoteStatus',
            headerName: 'Status',
            minWidth: 90,
            cellRenderer: getQuoteStatusTemplate,
        },
        {
            field: 'bom_name',
            headerName: 'BOM Name',
            cellRenderer: (params: ICellRendererParams) => {
                const { bom_id, bom_name, isPrimary, isLinked } = params.data;
                return <BomTemplate bomId={bom_id} bomName={bom_name} isPrimary={isPrimary} isLinked={isLinked} projectId={currentProjectId} target="_self" />
            },
            minWidth: 100,
        },
        {
            field: 'primary_contact',
            headerName: 'Primary Customer',
            cellRenderer: (params: ICellRendererParams) => Customer({ ...params.data.primary_contact, target: '_blank' }),
            minWidth: 100,
        },
        {
            field: 'quotes',
            headerName: "Quote Document (Version)",
            cellRenderer: getQuotesTemplate,
            minWidth: 115,
            flex: 1.5
        }
    ]), [getQuoteStatusTemplate, getQuotesTemplate, currentProjectId])

    const quotesDataArr = useMemo(() => {
        if (projectQuotesData?.data && projectBomsData?.data && enable_quoting && quotesTagData?.data && allTagsObject.size) {
            let lostQuoteId: number | null = null;
            const bomsArr = projectQuotesData.data.filter((project) => project.project.id === Number(currentProjectId));
            //  Insert all the BOM(s) with no quotes in the bomsArr.
            projectBomsData.data.forEach((bomItem) => {
                if (!bomItem.quote) {
                    bomsArr.push({
                        bom: {
                            created_at: bomItem.created_at,
                            id: bomItem.id,
                            name: bomItem.name,
                            primary_contact: bomItem.primary_contact,
                            is_primary: bomItem.is_primary,
                            created_by: bomItem.created_at,
                            is_linked: bomItem.is_linked,
                        }
                    } as any);
                }
            });

            if (selectedProjectStage === STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_STAGE_CLOSED_LOST) {
                lostQuoteId = getValueKeyFromMap(allTagsObject, STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.LOST);
            }

            const result = bomsArr.map((item) => {
                const { bom, quote } = item;
                const bomId = bom.id;
                let selectedQuoteId = null;
                let selectedQuoteStatus = null;

                // Proceed only if quote and quotesTagData exist
                if (quote?.length && quotesTagData?.data?.length) {
                    const quoteTagsByBom = quotesTagData.data.filter((tag) => tag.bom_id === bomId && tag.entity_type === QUOTE);

                    for (let quoteItem of quote) {
                        // Update quote with formatted quote number
                        quoteItem.quote_number += ` - ${convertDateFormat(quoteItem.created_at!)}`;

                        // Find matching quote tag
                        const matchingTag = quoteTagsByBom.find((tag) => tag.entity_id === quoteItem.id);
                        if (matchingTag) {
                            quoteItem.tag_name = matchingTag.tag_name;
                            quoteItem.tag_id = matchingTag.tag_id;
                            selectedQuoteId = quoteItem.id;
                            selectedQuoteStatus = matchingTag.tag_id;
                        }
                    }

                    if (!selectedQuoteId) {
                        selectedQuoteId = quote[0]?.id || null;
                    }

                    // If a quote is selected but no status, set default status
                    selectedQuoteStatus = selectedQuoteStatus ?? 0;

                    // If project stage is Closed Lot then set every in-progress BOM to status "Lost"                 
                    if (selectedProjectStage === STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_STAGE_CLOSED_LOST && selectedQuoteStatus === 0 && lostQuoteId) {
                        selectedQuoteStatus = lostQuoteId;
                    }
                }

                return {
                    bom_id: bom.id,
                    bom_name: bom.name,
                    quotes: quote,
                    selectedQuoteStatus,
                    selectedQuoteId,
                    isLinked: bom.is_linked,
                    primary_contact: bom.primary_contact,
                    isPrimary: bom.is_primary,
                };
            })

            const primaryBomIndex = result.findIndex((item) => item.isPrimary);
            if (primaryBomIndex !== -1) {
                const [primaryBom] = result.splice(primaryBomIndex, 1);
                result.unshift(primaryBom);
            }
            return result
        }

        return [];

    }, [projectQuotesData?.data, projectBomsData?.data, enable_quoting, quotesTagData?.data, allTagsObject, selectedProjectStage, currentProjectId]);

    useEffect(() => {
        if (!isEqual(quotesDataArr, quotesData)) {
            setQuotesData(quotesDataArr);
        }
    }, [quotesData, quotesDataArr]);

    function handleProjectStage(e: SelectChangeEvent<unknown>) {
        const { value } = e.target;
        if (value === STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_STAGE_REBID) {
            setShowBidDueDate(true);
        } else {
            setShowBidDueDate(false);
        }
        setProjectStage(value as string);
        setError({ error: '' });
    }

    const handleBidDueDate = (value: Dayjs) => {
        setBidDueDate(value);
    }

    async function handleOnSave() {
        if (quotesData.length) {
            if (allTagsObject.size) {
                switch (projectStage) {
                    case PROJECT_STAGE_TRIGGER_VALUES[0]:
                        if (validateProjectAndBomStatus(quotesData, allTagsObject)) {
                            setError({
                                error: STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_CLOSED_WON_ERROR_MSG
                            });
                            return;
                        }

                        break;

                    case PROJECT_STAGE_TRIGGER_VALUES[1]:
                        if (validateProjectAndBomStatus(quotesData, allTagsObject, true)) {
                            setError({
                                error: STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_CLOSED_LOST_ERROR_MSG
                            });
                            return;
                        }
                        break;

                    case PROJECT_STAGE_TRIGGER_VALUES[2]:
                        if (validateProjectAndBomStatus(quotesData, allTagsObject, true)) {
                            setError({
                                error: STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_CLOSED_ABANDONED_ERROR_MSG
                            });
                            return;
                        }
                        break;
                }
            }

            const quotesPayload: Array<IUpdateDocumentTag> = quotesData.map((item) => {
                return {
                    bom_id: String(item.bom_id),
                    entity_type: QUOTE,
                    entity_id: item.selectedQuoteId!,
                    tag_id: item?.selectedQuoteStatus || null
                };
            });

            try {
                await updateProjectQuotesTag({
                    projectId: String(currentProjectId),
                    payload: quotesPayload
                });

                const unlinkBomsArrMutationsFunc = quotesData.reduce((acc: Array<Promise<AxiosResponse>>, curr: ProjectQuotesInfo) => {
                    if (
                        curr.isLinked &&
                        curr?.selectedQuoteStatus &&
                        allTagsObject.size &&
                        QUOTE_DOCUMENT_AUTOMATION_STATUSES.some((status) => allTagsObject.get(curr.selectedQuoteStatus!) === status)
                    ) {
                        acc.push(
                            unLinkBomDetails({
                                bomId: String(curr.bom_id),
                                projectId: Number(currentProjectId)
                            })
                        );
                    }
                    return acc;
                }, []);

                await Promise.all(unlinkBomsArrMutationsFunc);
            } catch (e) {
                console.error(e)
            }
        }
        onUpdateProjectStage(projectStage, bidDueDate);
        onClose();
    }

    const getRowId = useCallback(
        (params: GetRowIdParams<ProjectQuotesInfo>) => String(params.data.bom_id),
        [],
    );

    const handleOnClose = (_: object, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason && reason === 'backdropClick') {
            return;
        }
        onClose();
    }

    const handleBulkActionsBtn = useCallback((type: ButtonTypes): void => {
        setError({ error: '' })
        if (!quotesData.length) return;
        const currentQuotesData = [...quotesData];
        let keyId: number | null = null;
        const selectedBomsSet = new Set(selectedBoms);
        switch (type) {
            case STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.WON:
                keyId = getValueKeyFromMap(allTagsObject, STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.WON);
                break;

            case STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.LOST:
                keyId = getValueKeyFromMap(allTagsObject, STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.LOST);
                break;

            case STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.ABANDONED:
                keyId = getValueKeyFromMap(allTagsObject, STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.ABANDONED);
                break;

            default:
                break;
        }

        if (keyId) {
            currentQuotesData.forEach((quote) => {
                if (selectedBomsSet.has(quote.bom_id) && quote.selectedQuoteId) {
                    quote.selectedQuoteStatus = keyId
                }
            })

            setQuotesData(currentQuotesData);
        }
    }, [allTagsObject, quotesData, selectedBoms])


    const onCheckboxChange = useCallback((params: SelectionChangedEvent) => {
        const selectedNodes = params.api.getSelectedNodes();
        setError({ error: '' })
        if (selectedNodes.length) {
            const bomIds = selectedNodes.map((item) => item.data.bom_id);
            setSelectedBoms(bomIds)
        } else {
            setSelectedBoms([])
        }
    }, [])

    const tableHeight = useMemo(() => {
        return getStatusReconciliationModalTableHeight({ rowHeight: 36, rows: quotesData.length, tableMaxHeight: 300 })
    }, [quotesData.length])

    const table = useMemo(() => {
        return <AgGridTable
            ref={gridRef}
            rowData={quotesData}
            columnDefs={columnDefs}
            isLoading={isTableLoading}
            noRowsOverlayComponent={CustomNoRowsOverlayComponent}
            rowSelection='multiple'
            suppressRowClickSelection={true}
            suppressContextMenu
            defaultColDef={STATUS_RECONCILIATION_POPUP_CONSTANTS.DEFAULT_COL_DEFS}
            getRowId={getRowId}
            showToolbarPanel={false}
            onSelectionChanged={onCheckboxChange}
            valueCache
        />
    }, [columnDefs, getRowId, isTableLoading, quotesData, onCheckboxChange])

    return (
        <>
            <Modal
                onClose={handleOnClose}
                open={open}
                header={<ModalHeader title={STATUS_RECONCILIATION_POPUP_CONSTANTS.MODAL_TITLE} onClose={onClose} />}
                footer={
                    <ModalFooter
                        helperText={
                            <Box >
                                <BodySmall color={'error'} fontWeight={500} limit={true} lines={2}>
                                    {error.error || ''}
                                </BodySmall>
                            </Box>
                        }
                        onAccept={handleOnSave}
                        onReject={onClose}
                        cancelButtonLabel="Cancel"
                        continueButtonLabel="Update"
                        isLoading={isFooterLoading}
                    />
                }
            >
                <Box display={'flex'} flexDirection={'column'}>

                    <BodySmall lines={2}>{STATUS_RECONCILIATION_POPUP_CONSTANTS.MODAL_DESCRIPTION}</BodySmall>
                    <Box display={'flex'} columnGap={4}>
                        <Box pt={4} pb={1} width={180}>
                            <BodySmall fontWeight={500}>Project Stage</BodySmall>
                            <Box width={1} pt={'8px'}>
                                <StatusSelect
                                    value={projectStage}
                                    id={'Project Stage'}
                                    labelId={'Project Stage'}
                                    name={'stage'}
                                    options={getProjectStages(true, enable_quoting) as unknown as IOptionType[]}
                                    optionLabelKeyname="label"
                                    optionValueKeyname="value"
                                    onChange={handleProjectStage}
                                    sx={{ height: 36 }}
                                />
                            </Box>
                        </Box>
                        {
                            showBidDueDate && <Box pt={4} pb={1}>
                                <BodySmall fontWeight={500}>Update Quote Due Date</BodySmall>
                                <Box width={1} pt={'8px'}>
                                    <CustomDateTimePicker
                                        value={bidDueDate}
                                        label={'Quote Due Date'}
                                        onAccept={handleBidDueDate}
                                        onChange={handleBidDueDate}
                                        shouldDisableDate={getIfWeekendDates}
                                    />
                                </Box>
                            </Box>
                        }
                    </Box>
                    <Box mt={3} width={1} display="flex" rowGap={1} flexDirection={'column'}>
                        <BodySmall fontWeight={500}>
                            Quotes Statuses
                        </BodySmall>

                        <Box display={'flex'} columnGap={2}>
                            <BodySmall>
                                {STATUS_RECONCILIATION_POPUP_CONSTANTS.LINK_TITLE}
                            </BodySmall>
                            <LearnMore url={STATUS_RECONCILIATION_POPUP_CONSTANTS.LINK_URL} linkText={STATUS_RECONCILIATION_POPUP_CONSTANTS.LINK_TEXT} />
                        </Box>
                    </Box>
                    <Box my={2} width={1} display={'flex'} columnGap={2} alignItems={'center'}>

                        <Box width={'20%'}>
                            <BodySmall>
                                Update to:
                            </BodySmall>
                        </Box>

                        <Tooltip title={!Boolean(selectedBoms.length) ? STATUS_RECONCILIATION_POPUP_CONSTANTS.BULK_ACTION_BUTTON_TOOL_TIP_MSG : ''}>
                            <Box display={'flex'} columnGap={2} >
                                <Button disabled={!Boolean(selectedBoms.length)} variant='outlined' color="secondary" size='small' onClick={() => handleBulkActionsBtn(STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.WON as ButtonTypes)}>
                                    {STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.WON}
                                </Button>


                                <Button disabled={!Boolean(selectedBoms.length)} variant='outlined' color="secondary" size='small' onClick={() => handleBulkActionsBtn(STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.LOST as ButtonTypes)}>
                                    {STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.LOST}
                                </Button>

                                <Button disabled={!Boolean(selectedBoms.length)} variant='outlined' color="secondary" size='small' onClick={() => handleBulkActionsBtn(STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.ABANDONED as ButtonTypes)}>
                                    {STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.ABANDONED}
                                </Button>
                            </Box>
                        </Tooltip>

                        <Box width={'100%'} height={'100%'} pt={2}>
                            <CustomToolBarPanel
                                selectedRowCount={selectedBoms.length}
                                loading={isTableLoading}
                                toolBarOptions={['selectedItems']}
                                disabledToolBarButton={false}
                            />
                        </Box>
                    </Box>

                    <Box width={'100%'} height={tableHeight} pt={1} sx={{ '& .e-gridcontent': { height: 'calc(100% - 44px) !important' } }} className="ag-theme-quartz">
                        {table}
                    </Box>

                    <BodySmall mt={-6} color="tertiary.dark" limit={false} fontWeight={700}>
                        Linked BOMs will be automatically unlinked when the Quote Status is changed to Won, Lost, or Abandoned.
                    </BodySmall>

                </Box>
            </Modal>
        </>
    );
}

export default memo(UpdateProjectQuoteStatusModal);